import { useCallback, useEffect, useState } from 'react'
import { useI18n } from '../lib/i18n'
import { corDoModo } from '../lib/sinalCores'

/**
 * Logbook.
 *
 * Segue o wireframe (docs/wireframes.html, tela 07): o contato em andamento
 * fica no topo, PRÉ-PREENCHIDO. Frequência, modo, banda, país e distância o
 * sistema já sabe; sobra indicativo e report. Um toque grava.
 *
 * A exportação sai sempre marcada como simulação — não há caminho que produza
 * arquivo sem marcador (docs/10-riscos-legal-lgpd.md §3).
 */

interface QSO {
  id: string
  callsign: string
  started_at: string
  band: string
  mode: string
  country?: string
  distance_km?: number
  bearing_deg?: number
  rst_sent?: string
  rst_rcvd?: string
  audio_assist: string
}

interface Stats {
  qsos: number
  countries: number
  bands: number
  for_awards: number
  country_list: string[]
}

export function Logbook({
  callsign,
  freqKHz,
  mode,
  lat,
  lon,
  suggested,
  onSaved,
}: {
  callsign: string
  freqKHz: number
  mode: string
  lat: number
  lon: number
  /** Indicativo da estação no ar agora, para pré-preencher. */
  suggested?: string
  /** Avisa que o log mudou, para os diplomas recalcularem. */
  onSaved?: () => void
}) {
  const { t } = useI18n()
  const [qsos, setQsos] = useState<QSO[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [their, setTheir] = useState('')
  const [rstSent, setRstSent] = useState('59')
  const [rstRcvd, setRstRcvd] = useState('59')
  const [msg, setMsg] = useState<string | null>(null)

  const load = useCallback(() => {
    fetch('/api/qsos')
      .then((r) => r.json())
      .then((d: { qsos: QSO[] | null; stats: Stats }) => {
        setQsos(d.qsos ?? [])
        setStats(d.stats)
      })
      .catch(() => {})
  }, [])

  useEffect(load, [load])

  // Pré-preenche com quem está no ar, sem sobrescrever o que já foi digitado.
  useEffect(() => {
    if (suggested && !their) setTheir(suggested)
  }, [suggested, their])

  const save = async () => {
    setMsg(null)
    try {
      const r = await fetch('/api/qsos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          callsign: their,
          freq_khz: freqKHz,
          mode,
          rst_sent: rstSent,
          rst_rcvd: rstRcvd,
          my_lat: lat,
          my_lon: lon,
          power_w: 100,
        }),
      })
      if (r.status === 409) {
        setMsg(t.logbook.jaHaContato)
        return
      }
      if (!r.ok) {
        const e = (await r.json()) as { message?: string }
        setMsg(e.message ?? t.logbook.erroGravar)
        return
      }
      setTheir('')
      setMsg(null)
      load()
      onSaved?.()
    } catch {
      setMsg(t.logbook.erroGravar)
    }
  }

  return (
    <section className="logbook">
      <h2>{t.logbook.titulo}</h2>

      {stats && (
        <div className="log-stats">
          <span>
            <b>{stats.qsos}</b> {t.logbook.qso}
          </span>
          <span>
            <b>{stats.countries}</b> {t.logbook.paises}
          </span>
          <span>
            <b>{stats.bands}</b> {t.logbook.bandas}
          </span>
          <span>
            <b>{stats.for_awards}</b> {t.logbook.pDiploma}
          </span>
        </div>
      )}

      <div className="log-entry">
        <input
          value={their}
          onChange={(e) => setTheir(e.target.value.toUpperCase())}
          placeholder={t.logbook.indicativoPlaceholder}
          aria-label={t.logbook.indicativoAria}
        />
        <input
          value={rstSent}
          onChange={(e) => setRstSent(e.target.value)}
          aria-label={t.logbook.rstEnviado}
          className="rst"
        />
        <input
          value={rstRcvd}
          onChange={(e) => setRstRcvd(e.target.value)}
          aria-label={t.logbook.rstRecebido}
          className="rst"
        />
        <button onClick={() => void save()} disabled={!their}>
          {t.logbook.gravar}
        </button>
      </div>
      <p className="log-hint">
        {(freqKHz / 1000).toFixed(3)} MHz {mode} · {t.logbook.hintSufixo}
      </p>
      {msg && <p className="denial">{msg}</p>}

      {qsos.length > 0 && (
        <ul className="log-list">
          {qsos.slice(0, 6).map((q) => (
            <li key={q.id}>
              <strong>{q.callsign}</strong>
              <span className="pill">{q.band}</span>
              <span className="pill" style={{ color: corDoModo(q.mode) }}>{q.mode}</span>
              <span className="grow" />
              {q.country && <span>{q.country}</span>}
              {q.distance_km !== undefined && <span>{q.distance_km.toFixed(0)} km</span>}
              {q.audio_assist === 'clear' && <span className="pill">{t.logbook.claro}</span>}
            </li>
          ))}
        </ul>
      )}

      <a
        className="log-export"
        href={`/api/qsos/adif?callsign=${encodeURIComponent(callsign)}`}
        download
      >
        {t.logbook.exportarADIF}
        <small>{t.logbook.exportarHint}</small>
      </a>
    </section>
  )
}
